import React from "react";
import { Skeleton } from "antd";

import { fmtNum, HBar, MetricCard, SectionCard, useMasteryApi } from "./shared";

// Ported from the standalone Golden Explorer's Overview view (/api/overview →
// mastery-overview/). The health block lives on its own surface
// (DataMasteryHealth); steward decisions flow through the queue, so there is
// no "resolved locally" metric here.

interface RunInfo {
  run_id: string | null;
  model_version: string | null;
  status: string | null;
  started_at: string | null;
  finished_at: string | null;
  notes: string | null;
}

interface Funnel {
  raw_sites: number | null;
  normalized: number | null;
  candidate_pairs: number | null;
  scored: number | null;
  auto_accepted: number | null;
  human_review: number | null;
  auto_rejected: number | null;
  refused_unions: number | null;
  golden_sites: number | null;
  golden_people: number | null;
  institutions: number | null;
}

export interface MasteryOverviewPayload {
  run: RunInfo;
  funnel: Funnel;
  tiers: Array<{ tier: string | null; n: number }>;
  sources: Array<{ source_system: string; n: number }>;
  queue: Array<{ item_type: string; n: number; open: number }>;
}

// decided (kept/dropped) vs. still-in-flight funnel steps
const ACCEPT_COLOR = "#0E7490";
const DROP_COLOR = "#94a3b8";

export default function MasteryOverview() {
  const { data, isLoading } =
    useMasteryApi<MasteryOverviewPayload>("mastery-overview/");

  if (isLoading || !data) {
    return <Skeleton active paragraph={{ rows: 12 }} />;
  }

  const f = data.funnel;
  const steps = [
    { label: "Raw records", value: f.raw_sites, color: ACCEPT_COLOR },
    { label: "Normalized", value: f.normalized, color: ACCEPT_COLOR },
    { label: "Candidates", value: f.candidate_pairs, color: ACCEPT_COLOR },
    { label: "Scored", value: f.scored, color: ACCEPT_COLOR },
    { label: "Accepted", value: f.auto_accepted, color: ACCEPT_COLOR },
    { label: "Review", value: f.human_review, color: "#D97706" },
    { label: "Rejected", value: f.auto_rejected, color: DROP_COLOR },
    { label: "Refused", value: f.refused_unions, color: DROP_COLOR },
    { label: "Golden sites", value: f.golden_sites, color: "#B8860B" },
  ];
  const max = Math.max(...steps.map((s) => s.value ?? 0), 1);
  const tierTotal = data.tiers.reduce((acc, t) => acc + t.n, 0);
  const queueOpen = data.queue.reduce((acc, q) => acc + q.open, 0);
  const sourceTotal = data.sources.reduce((acc, s) => acc + s.n, 0);

  return (
    <div>
      {/* Entities, tiers, queue — breakdowns as first-class rows */}
      <div className="mb-4 grid grid-cols-3 gap-4">
        <MetricCard
          label="Golden entities"
          value={fmtNum(f.golden_sites)}
          rows={[
            { label: "Golden sites", value: fmtNum(f.golden_sites) },
            { label: "Golden people", value: fmtNum(f.golden_people) },
            { label: "Institutions", value: fmtNum(f.institutions) },
          ]}
        />
        <MetricCard
          label="Admission tiers"
          value={fmtNum(tierTotal)}
          rows={data.tiers.map((t) => ({
            label: t.tier ?? "untiered",
            value: fmtNum(t.n),
          }))}
        />
        <MetricCard
          label="Review queue open"
          value={fmtNum(queueOpen)}
          rows={data.queue.map((q) => ({
            label: q.item_type.replace(/_/g, " "),
            value: `${fmtNum(q.open)} / ${fmtNum(q.n)}`,
          }))}
        />
      </div>

      {/* Pipeline funnel */}
      <SectionCard
        title="Pipeline funnel"
        meta="raw records → scored pairs → golden entities"
      >
        {steps.map((s) => (
          <HBar
            key={s.label}
            label={s.label}
            n={s.value ?? 0}
            max={max}
            color={s.color}
          />
        ))}
      </SectionCard>

      {/* Source systems */}
      <SectionCard
        title="Source systems"
        meta="raw site records per vendor feed"
      >
        <table className="w-full border-collapse text-xs">
          <thead>
            <tr className="text-[#556C75]">
              <th className="p-2 text-left">Source</th>
              <th className="p-2 text-right">Raw records</th>
              <th className="p-2 text-right">Share</th>
            </tr>
          </thead>
          <tbody>
            {data.sources.map((s) => (
              <tr key={s.source_system} className="border-t border-[#f0f0f0]">
                <td className="p-2 font-medium text-[#2B4752]">
                  {s.source_system}
                </td>
                <td className="p-2 text-right tabular-nums">{fmtNum(s.n)}</td>
                <td className="p-2 text-right tabular-nums">
                  {sourceTotal
                    ? `${((100 * s.n) / sourceTotal).toFixed(1)}%`
                    : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </SectionCard>
    </div>
  );
}
